import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { API } from '@/App';
import axios from 'axios';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, Filter, RefreshCw, Clock, Globe, XCircle, Copy, Check, Wrench, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const severityConfig = {
  critical: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20', rowBg: 'bg-red-500/5 dark:bg-red-500/10' },
  warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', rowBg: 'bg-amber-500/5 dark:bg-amber-500/10' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20', rowBg: 'bg-blue-500/5 dark:bg-blue-500/10' },
};

const SeverityBadge = ({ severity }) => {
  const cfg = severityConfig[severity] || severityConfig.info;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} ${cfg.border} border`}>
      <Icon className="h-3 w-3" />
      {severity}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const isActive = status === 'active';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      isActive ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-red-500' : 'bg-emerald-500'}`} />
      {isActive ? 'Active' : 'Resolved'}
    </span>
  );
};

const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: '', status: '', network: '' });
  const [page, setPage] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const limit = 20;

  const copyMessage = (id, message) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(message).catch(() => {
          _fallbackCopy(message);
        });
      } else {
        _fallbackCopy(message);
      }
    } catch {
      _fallbackCopy(message);
    }
    setCopiedId(id);
    toast.success('Message copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const _fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);
      if (filters.network) params.append('network', filters.network);
      params.append('limit', limit);
      params.append('skip', page * limit);
      const { data } = await axios.get(`${API}/admin/alerts?${params.toString()}`);
      setAlerts(data.alerts);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleResolve = async (alertId) => {
    try {
      await axios.post(`${API}/admin/alerts/${alertId}/resolve`);
      fetchAlerts();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout title="System Alerts">
      <div className="space-y-5" data-testid="alerts-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Alerts', value: total, icon: AlertCircle, color: 'text-foreground' },
            { label: 'Active', value: alerts.filter(a => a.status === 'active').length, icon: XCircle, color: 'text-red-500' },
            { label: 'Resolved', value: alerts.filter(a => a.status === 'resolved').length, icon: CheckCircle2, color: 'text-emerald-500' },
          ].map((card) => (
            <div key={card.label} className="bg-card border border-gray-200 dark:border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid={`alert-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className={`w-10 h-10 rounded-lg ${card.color === 'text-red-500' ? 'bg-red-500/10' : card.color === 'text-emerald-500' ? 'bg-emerald-500/10' : 'bg-primary/10'} flex items-center justify-center`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{card.label}</p>
                <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 bg-card border border-gray-200 dark:border-white/10 rounded-xl p-4" data-testid="alerts-filters">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={filters.severity}
            onChange={(e) => { setFilters(f => ({ ...f, severity: e.target.value })); setPage(0); }}
            className="h-9 px-3 bg-background border border-gray-200 dark:border-white/10 rounded-lg text-sm text-foreground"
            data-testid="filter-severity"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }}
            className="h-9 px-3 bg-background border border-gray-200 dark:border-white/10 rounded-lg text-sm text-foreground"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={filters.network}
            onChange={(e) => { setFilters(f => ({ ...f, network: e.target.value })); setPage(0); }}
            className="h-9 px-3 bg-background border border-gray-200 dark:border-white/10 rounded-lg text-sm text-foreground"
            data-testid="filter-network"
          >
            <option value="">All Networks</option>
            <option value="BSC">BSC</option>
          </select>
          <button onClick={() => { fetchAlerts(); }} className="ml-auto flex items-center gap-1.5 h-9 px-3 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors" data-testid="refresh-alerts-btn">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="bg-card border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden" data-testid="alerts-table-container">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10 bg-muted/30">
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[100px]">Severity</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[160px]">Type</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Message</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[90px]">Network</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[100px]">Status</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[100px]">Created</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[100px]">Resolved</th>
                  <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 w-[80px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">Loading alerts...</td></tr>
                ) : alerts.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12">
                    <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No alerts found. All systems operational.</p>
                  </td></tr>
                ) : alerts.map((alert) => {
                  const cfg = severityConfig[alert.severity] || severityConfig.info;
                  const isResolved = alert.status === 'resolved';
                  const recoveryActions = alert.meta?.recovery || [];
                  const hasRecovery = Array.isArray(recoveryActions) && recoveryActions.length > 0;
                  const isExpanded = expandedId === alert.id;
                  return (
                    <React.Fragment key={alert.id}>
                    <tr
                      className={`border-b border-gray-200 dark:border-white/10 transition-colors ${
                        isResolved ? 'opacity-60' : cfg.rowBg
                      } ${hasRecovery ? 'cursor-pointer' : ''}`}
                      data-testid={`alert-row-${alert.id}`}
                      onClick={() => hasRecovery && setExpandedId(isExpanded ? null : alert.id)}
                    >
                      <td className="px-4 py-3"><SeverityBadge severity={alert.severity} /></td>
                      <td className="px-4 py-3 text-sm font-mono text-foreground">{alert.type}</td>
                      <td className="px-4 py-3 text-sm text-foreground overflow-hidden">
                        <div className="flex items-center gap-2 max-w-full overflow-hidden">
                          <button
                            onClick={(e) => { e.stopPropagation(); copyMessage(alert.id, alert.message); }}
                            className="group flex items-center gap-2 text-left cursor-pointer hover:text-primary transition-colors overflow-hidden flex-1 min-w-0"
                            title="Click to copy full message"
                            data-testid={`copy-message-${alert.id}`}
                          >
                            <span className="truncate block">{alert.message}</span>
                            {copiedId === alert.id
                              ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                              : <Copy className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 shrink-0 transition-opacity" />}
                          </button>
                          {hasRecovery && (
                            <span className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 text-[10px] font-semibold" data-testid={`recovery-badge-${alert.id}`}>
                              <Wrench className="h-2.5 w-2.5" />
                              {recoveryActions.length}
                              <ChevronDown className={`h-2.5 w-2.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                          <Globe className="h-3 w-3" /> {alert.network}
                        </span>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={alert.status} /></td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" /> {formatTime(alert.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatTime(alert.resolved_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {alert.status === 'active' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResolve(alert.id); }}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors"
                            data-testid={`resolve-alert-${alert.id}`}
                          >
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                    {hasRecovery && isExpanded && (
                      <tr className="bg-emerald-500/5 dark:bg-emerald-500/5" data-testid={`recovery-row-${alert.id}`}>
                        <td colSpan={8} className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <Wrench className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-emerald-500 mb-1.5">Self-Healing Actions Taken</p>
                              <ul className="space-y-1">
                                {recoveryActions.map((action, i) => (
                                  <li key={i} className="text-sm text-foreground flex items-start gap-2">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                                    <span>{action}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-white/10" data-testid="alerts-pagination">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-white/10 text-foreground disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-white/10 text-foreground disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
